<script setup>
// Visible breadcrumb trail that mirrors the BreadcrumbList JSON-LD emitted
// in app.blade.php from the shared Inertia `breadcrumb` prop. Renders only
// when the trail has >= 2 items (homepage + something). Schema and visible
// markup must stay in sync per Google's enhancement rules - both are derived
// from the same App\Services\BreadcrumbBuilder output, so the only thing
// this component does is render the array.
import { computed } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';

const props = defineProps({
    items: { type: Array, default: null },
});

const page = usePage();
const trail = computed(() => {
    if (Array.isArray(props.items) && props.items.length >= 2) return props.items;
    const shared = page.props?.breadcrumb;
    if (shared && Array.isArray(shared.items) && shared.items.length >= 2) return shared.items;
    return null;
});
</script>

<template>
    <nav v-if="trail" class="bc" aria-label="Breadcrumb">
        <ol class="bc__list">
            <li v-for="(item, i) in trail" :key="i" class="bc__item">
                <Link v-if="i < trail.length - 1" :href="item.url" class="bc__link">{{ item.name }}</Link>
                <span v-else class="bc__current" aria-current="page">{{ item.name }}</span>
                <span v-if="i < trail.length - 1" class="bc__sep" aria-hidden="true">/</span>
            </li>
        </ol>
    </nav>
</template>

<style scoped>
.bc {
    max-width: 1240px;
    margin: 0 auto;
    padding: 12px 24px 0;
    font-size: 13px;
    color: var(--brand-ink-3, #64748B);
    line-height: 1.4;
}
.bc__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.bc__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.bc__link {
    color: inherit;
    text-decoration: none;
    transition: color 0.15s ease;
}
.bc__link:hover {
    color: var(--brand-accent, var(--sav-accent-deep, #1f3a5f));
    text-decoration: underline;
    text-underline-offset: 2px;
}
.bc__current {
    color: var(--brand-ink, #0F1A35);
    font-weight: 600;
}
.bc__sep {
    color: var(--brand-line, #cbd5e1);
}
@media (max-width: 560px) {
    .bc { padding: 8px 16px 0; font-size: 12px; }
}
</style>
